import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { fmtMonthDay, fmtRelativeDay, type SubmitStats } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { todayCN } from '../../lib/clock';
import { useLayout } from '../../lib/media';
import { useDashboard } from '../../lib/queries';
import { Icon, StatusIcon } from '../../ui/icons';
import { EmptyState, Skeleton } from '../../ui/primitives';
import './dashboard.css';

// 管理层看板：日报提交、项目状态、待处理的异常和指派。范围和完整日报的可见范围一致。

const pct = (s: SubmitStats) => (s.expected ? Math.round(((s.onTime + s.late) / s.expected) * 100) : null);
const onTimePct = (s: SubmitStats) => (s.expected ? Math.round((s.onTime / s.expected) * 100) : null);

export function DashboardPage() {
  const mobile = useLayout() === 'mobile';
  const navigate = useNavigate();
  const { data: d, isLoading, error } = useDashboard();
  const today = todayCN();

  const body = isLoading ? (
    <div style={{ padding: 4 }} aria-busy="true">
      <Skeleton h={14} w={240} />
    </div>
  ) : error || !d ? (
    <EmptyState icon="lock" title="看板没有加载出来" desc={errorText(error)} />
  ) : (
    <>
      <div className="db-tiles">
        <Tile
          k="今日日报"
          v={d.today.workday ? `${d.today.submitted}/${d.today.expected}` : '休息日'}
          sub={
            !d.today.workday
              ? '今天不用写日报'
              : d.today.pending.length
                ? `${d.today.pending.length} 人未交 · ${d.today.afterDeadline ? '已过' : ''}${d.today.deadline} 截止`
                : '都交了'
          }
          warn={d.today.afterDeadline && d.today.pending.length > 0}
        />
        <Tile k="本周提交率" v={pct(d.week.total) == null ? '—' : `${pct(d.week.total)}%`} sub={`按时 ${onTimePct(d.week.total) ?? '—'}% · 晚交 ${d.week.total.late} · 未交 ${d.week.total.missing}`} />
        <Tile k="进行中的项目" v={String(d.projects.progress)} sub={`阻塞 ${d.projects.blocked} · 未开始 ${d.projects.todo} · 本月完成 ${d.projects.doneThisMonth}`} onClick={() => navigate('/radar')} />
        <Tile
          k="待处理的异常"
          v={String(d.issues.openAnomalies)}
          sub={`本周新增 ${d.issues.newThisWeek} · 关闭 ${d.issues.closedThisWeek}${d.issues.oldestOpenDays ? ` · 最久 ${d.issues.oldestOpenDays} 天` : ''}`}
          onClick={() => navigate('/inbox')}
        />
        <Tile k="进行中的指派" v={String(d.issues.openAssignments)} sub={d.issues.overdueAssignments ? `${d.issues.overdueAssignments} 件已过期限` : '没有过期的'} warn={d.issues.overdueAssignments > 0} />
      </div>

      {d.today.pending.length > 0 && (
        <Block title={d.today.afterDeadline ? '今天没交日报' : '今天还没交日报'}>
          <div className="db-names">
            {d.today.pending.map((p) => (
              <Link key={p.id} to={`/people/${p.id}`} className="db-name">
                {p.name}
              </Link>
            ))}
          </div>
        </Block>
      )}

      <div className="db-cols">
        <Block title="本周各团队日报" meta={`${fmtMonthDay(d.week.weekStart)} 起 · ${d.people} 人`}>
          {d.week.teams.length === 0 ? (
            <div className="t-meta">还没有需要写日报的成员</div>
          ) : (
            <table className="db-table">
              <thead>
                <tr>
                  <th>团队</th>
                  <th>人数</th>
                  <th>按时</th>
                  <th>晚交</th>
                  <th>未交</th>
                  <th>提交率</th>
                </tr>
              </thead>
              <tbody>
                {d.week.teams.map((t) => (
                  <tr key={t.teamId ?? 'none'}>
                    <td className="c1">{t.name}</td>
                    <td>{t.people}</td>
                    <td>{t.onTime}</td>
                    <td>{t.late || '—'}</td>
                    <td className={clsx(t.missing > 0 && 'c1')}>{t.missing || '—'}</td>
                    <td>{pct(t) == null ? '—' : `${pct(t)}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Block>

        <Block title="近 6 周" meta="柱高是提交率，深色部分是按时">
          <div className="db-trend">
            {d.trend.map((w) => {
              const all = pct(w);
              const ok = onTimePct(w);
              return (
                <div key={w.weekStart} className="db-week" title={`${fmtMonthDay(w.weekStart)} 那周：应交 ${w.expected}，按时 ${w.onTime}，晚交 ${w.late}，未交 ${w.missing}；新增异常 ${w.anomalies}`}>
                  <span className="pct">{all == null ? '—' : `${all}%`}</span>
                  <div className="bar">
                    <div className="all" style={{ height: `${all ?? 0}%` }}>
                      <div className="ok" style={{ height: all ? `${((ok ?? 0) / all) * 100}%` : 0 }} />
                    </div>
                  </div>
                  <span className="lbl">{w.weekStart === d.week.weekStart ? '本周' : fmtMonthDay(w.weekStart)}</span>
                  <span className="anom">{w.expected ? `${w.expected} 份${w.anomalies ? ` · 异常 ${w.anomalies}` : ''}` : ' '}</span>
                </div>
              );
            })}
          </div>
        </Block>
      </div>

      <div className="db-cols">
        <Block title="需要关注的项目" meta={d.projects.flagged.length ? `${d.projects.flagged.length} 个有健康信号` : undefined}>
          {d.projects.flagged.length === 0 ? (
            <div className="t-meta">没有阻塞、停滞或反复延期的项目</div>
          ) : (
            d.projects.flagged.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="db-row">
                <StatusIcon status={p.status} />
                <span className="t-meta code">{p.code}</span>
                <span className="name ellipsis">{p.name}</span>
                <span className="t-meta who">{p.ownerName}</span>
                <span className="sig">{p.signals.join(' · ')}</span>
              </Link>
            ))
          )}
        </Block>
        <Block title="两周内到期" meta={d.projects.due.some((p) => p.overdue) ? `${d.projects.due.filter((p) => p.overdue).length} 个已过截止日` : undefined}>
          {d.projects.due.length === 0 ? (
            <div className="t-meta">两周内没有到期的项目</div>
          ) : (
            d.projects.due.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="db-row">
                <Icon name="cal" size={14} color="var(--icon)" />
                <span className="t-meta code">{p.code}</span>
                <span className="name ellipsis">{p.name}</span>
                <span className="t-meta who">{p.ownerName}</span>
                <span className={clsx('sig', p.overdue && 'c1')}>{p.overdue ? `已过 ${fmtMonthDay(p.dueDate)}` : fmtRelativeDay(p.dueDate, today)}</span>
              </Link>
            ))
          )}
        </Block>
      </div>
    </>
  );

  return (
    <>
      {!mobile && (
        <TopBar crumbs={['看板']} icon="board">
          {d && (
            <span className="t-meta">
              范围：{d.scope} · {d.people} 人
            </span>
          )}
        </TopBar>
      )}
      <div className={clsx('db', mobile && 'mobile')}>
        {mobile && (
          <>
            <button type="button" className="db-back" onClick={() => navigate('/me')}>
              <Icon name="chevL" size={18} color="var(--text-3)" />
              <span>我</span>
            </button>
            <div className="db-title">看板</div>
            {d && <div className="t-meta" style={{ padding: '0 20px 8px' }}>范围：{d.scope} · {d.people} 人</div>}
          </>
        )}
        {body}
      </div>
    </>
  );
}

function Tile({ k, v, sub, warn, onClick }: { k: string; v: string; sub: string; warn?: boolean; onClick?: () => void }) {
  const inner = (
    <>
      <span className="k">{k}</span>
      <span className={clsx('v', warn && 'warn')}>{v}</span>
      <span className="sub">{sub}</span>
    </>
  );
  return onClick ? (
    <button type="button" className="db-tile link" onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className="db-tile">{inner}</div>
  );
}

function Block({ title, meta, children }: { title: string; meta?: string; children: ReactNode }) {
  return (
    <section className="db-block">
      <div className="db-block-head">
        <span className="name">{title}</span>
        {meta && <span className="t-meta">{meta}</span>}
      </div>
      {children}
    </section>
  );
}
